import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EntityTypeLabelPair, IEntityMeta, IFieldMeta, IMetaData } from '../../interfaces/imetadata';
import { IMetaDataServiceImpl } from './imetadataserviceimpl';

interface IObjectMeta {
  objectName: string;
  label: string;
  fields: any[];
}

interface IEndpoint {
  baseUrl: string;
  authHeader: { headers: { Authorization: string } };
}

class Monitor {
  maxDepth = 0;
  data: { entityType: string; result: IEntityMeta }[] = [];
  cancel = false;
  timerName: string;
  private _count = 0;
  private loadQueueSubject: Subject<{ length: number; error: boolean }>;
  private _peakQueueLength = 0;

  constructor(maxDepth: number, timerName: string, loadQueue: Subject<{ length: number; error: boolean }>) {
    this.maxDepth = maxDepth;
    this.timerName = timerName;
    this.loadQueueSubject = loadQueue;
  }

  get count() {
    return this._count;
  }

  set count(v: number) {
    this._count = v;
    this.loadQueueSubject.next({ length: this._count, error: this.cancel });
    this._peakQueueLength = this._count > this._peakQueueLength ? this._count : this._peakQueueLength;
  }

  get peakQueueLength() {
    return this._peakQueueLength;
  }
}

export abstract class BaseMetaDataService {
  private static TIMER_NAME = 'meta-load';
  private static MAX_DEPTH = 3;
  private static timerId = 1;

  private static createTimerName(name: string): string {
    return `${BaseMetaDataService.TIMER_NAME}-${name}-${BaseMetaDataService.timerId++}`;
  }

  protected constructor(
    protected http: HttpClient,
    private readonly serviceNamespace: string | undefined,
    protected readonly metaDataServiceImpl: IMetaDataServiceImpl
  ) {}

  private loadQueueSubject = new Subject<{ length: number; error: boolean }>();
  loadQueue$ = this.loadQueueSubject.asObservable();

  protected resolveNamespace(namespaceOverride?: string): string {
    const namespace = namespaceOverride ?? this.serviceNamespace;
    if (!namespace) {
      throw new Error('Namespace is required for metadata requests.');
    }
    return namespace;
  }

  // Convenience wrapper for fetching a single entity with an optional max depth override.
  getEntityMeta(token: string, entityName: string, maxDepthOverride?: number, namespaceOverride?: string): Observable<IEntityMeta> {
    return this.getEntityFields(token, entityName, undefined, maxDepthOverride, namespaceOverride).pipe(
      map((metaData) => metaData.entities.find((e) => e.entity === entityName)!)
    );
  }

  // Attach a fetched entity's fields to an associated field without triggering recursive fetches.
  applyAssociatedEntityMeta(associatedField: IFieldMeta, entityMeta: IEntityMeta): void {
    const children = entityMeta.fields.map((f) => ({ ...f, fields: f.fields?.map((cf) => ({ ...cf })) }));
    this.setChildren(associatedField, children);
  }

  getEntities(token: string, namespaceOverride?: string): Observable<EntityTypeLabelPair[]> {
    const namespace = this.resolveNamespace(namespaceOverride);
    const url = `${environment.kpHost}/datasource/${namespace}/meta?labels=true`;
    const authHeader = { headers: { Authorization: 'bearer ' + token } };
    return this.http
      .get<{
        namespace: string;
        objectNames: { objectName: string; label?: string; iconUrl?: string; iconColor?: string }[];
      }>(url, authHeader)
      .pipe(
        map((resp) => {
          return resp.objectNames
            .map((objectName) => ({
              entity: objectName.objectName,
              label: objectName.label ?? '',
              properties: {
                iconUrl: objectName.iconUrl,
                iconColor: objectName.iconColor,
              },
            }))
            .sort((a, b) => a.label?.localeCompare(b.label));
        })
      );
  }

  // maxDepthOverride lets callers control how far associated metadata should be eagerly fetched.
  getEntityFields(
    token: string,
    entityName: string,
    skipTypes?: string[],
    maxDepthOverride?: number,
    namespaceOverride?: string
  ): Observable<IMetaData> {
    const namespace = this.resolveNamespace(namespaceOverride);
    const timerName = BaseMetaDataService.createTimerName(entityName);
    console.time(timerName);
    const dataSubject = new Subject<IMetaData>();
    const data$ = dataSubject.asObservable();

    let associatedFieldFound = false;
    const setAssociatedFlag = () => (associatedFieldFound = true);
    const meta: IMetaData = { namespace: namespace, entities: [] };

    // Use override when we want lazy loading (e.g., Salesforce); otherwise default depth.
    const maxDepth = maxDepthOverride ?? BaseMetaDataService.MAX_DEPTH;
    const monitor = new Monitor(maxDepth, timerName, this.loadQueueSubject);
    const allMetaDataLoaded = new Subject<boolean>();
    this.onAllMetaLoaded(allMetaDataLoaded, meta, monitor, dataSubject); // triggered via allMetaDataLoaded subject

    const url = `${environment.kpHost}/datasource/${namespace}/meta/${entityName}`;
    const authHeader = { headers: { Authorization: 'bearer ' + token } };
    const endpoint = { baseUrl: url, authHeader: authHeader };

    this.http.get<IObjectMeta>(`${url}`, authHeader).subscribe({
      next: (objectMeta) => {
        if (skipTypes) {
          objectMeta.fields = objectMeta.fields.filter((f) => !skipTypes.includes(f.type));
        }

        this.loadMetaForEntities([objectMeta], meta, endpoint, monitor, allMetaDataLoaded, setAssociatedFlag);
      },
      error: (error) => {
        console.timeEnd(timerName);
        dataSubject.error(error);
      },
      complete: () => {
        // need to signal caller if the entity does not have any to-many fields
        if (!associatedFieldFound) {
          console.timeEnd(timerName);
          dataSubject.next(meta);
          dataSubject.complete();
        }
      },
    });

    return data$;
  }

  private loadMetaForEntities(
    objectMetaArray: IObjectMeta[],
    meta: IMetaData,
    endpoint: IEndpoint,
    monitor: Monitor,
    allMetaDataLoaded: Subject<boolean>,
    associatedFieldFound: () => void
  ): void {
    // add the main entities first as we have already pulled them
    objectMetaArray.forEach((objectMeta: IObjectMeta) => {
      const entry: { entityType: string; result: any } = {
        entityType: objectMeta.objectName,
        result: {
          entity: objectMeta.objectName,
          label: objectMeta.label,
          fields: this.mapObjectMeta(objectMeta),
        },
      };
      monitor.data.push(entry);
    });

    // now pull the field data
    objectMetaArray.forEach((objectMeta) => {
      const entityMeta: IEntityMeta = {
        namespace: meta.namespace,
        entity: objectMeta.objectName,
        label: objectMeta.label,
        fields: this.mapObjectMeta(objectMeta),
      };
      this.setParent(entityMeta.fields);
      entityMeta.fields.sort(this.fieldSort);
      meta.entities.push(entityMeta);

      // recursively load each child to-many or to-one fields
      this.debug('Loading meta for root entity', entityMeta.label);
      entityMeta.fields.forEach((field) => {
        this.metaDataServiceImpl.promoteCompositeFields(field);
        this.metaDataServiceImpl.updatePickerFieldMeta(field);

        // Only eager-fetch associated metadata when maxDepth allows it.
        if (monitor.maxDepth > 0 && this.isAssociatedField(field)) {
          associatedFieldFound();
          this.getAssociatedFieldMeta(endpoint, field, monitor, 0, allMetaDataLoaded);
        }
      });
    });
  }

  private onAllMetaLoaded(signal: Subject<boolean>, meta: IMetaData, monitor: Monitor, dataSubject: Subject<IMetaData>): void {
    signal.subscribe({
      next: (_) => {
        // the complete set of entities - should be immutable - should NEVER have: parent or fields properties!
        // console.log(monitor.data);

        this.debug(`Pulled ${monitor.data.length} unique entities.`);
        this.debug(`Peak Queue Length: ${monitor.peakQueueLength}`);

        meta.entities.forEach((entityMeta) => {
          entityMeta.fields.forEach((field) => {
            if (this.isAssociatedField(field)) {
              this.updateAssociatedField(field, monitor.data, 0, monitor.maxDepth);
            }
          });
        });
      },
      error: (err) => {
        monitor.cancel = true;
        console.error(err);
        console.timeEnd(monitor.timerName);
        dataSubject.error(err);
      },
      complete: () => {
        console.timeEnd(monitor.timerName);
        dataSubject.next(meta);
        dataSubject.complete();
        this.debug('All meta load complete.');
      },
    });
  }

  private getAssociatedFieldMeta(
    endpoint: IEndpoint,
    desiredAssociatedField: IFieldMeta,
    monitor: Monitor,
    deep: number,
    done: Subject<boolean>
  ): boolean {
    if (monitor.cancel) {
      return false;
    }
    desiredAssociatedField.associatedEntity = this.metaDataServiceImpl.getAssociatedEntity?.(desiredAssociatedField);
    const entityType = desiredAssociatedField.associatedEntity ?? desiredAssociatedField.path;
    const found = monitor.data.find((d) => d.entityType.toLowerCase() === entityType?.toLowerCase());
    if (!found) {
      this.debug('Queue pulling meta for', desiredAssociatedField.type.toUpperCase(), entityType, monitor.count + 1);
      const entry: { entityType: string; result: any } = {
        entityType: entityType,
        result: undefined,
      };
      monitor.data.push(entry);
      monitor.count++;
      const subscription = this.http
        .get<IObjectMeta>(`${endpoint.baseUrl}/${entityType}`, endpoint.authHeader)
        .pipe(
          map((om) => ({
            entity: om.objectName,
            label: om.label,
            fields: [...om.fields],
          }))
        )
        .subscribe({
          next: (entityObjectMeta) => {
            if (monitor.cancel) {
              subscription.unsubscribe();
              this.debug('Call to', entityObjectMeta.label, 'complete, but operation cancelled.');
              return;
            }

            this.debug('Meta pulled for', entityType, monitor.count - 1);

            monitor.count--;
            entry.result = entityObjectMeta;
            entityObjectMeta.fields = entityObjectMeta.fields ?? [];
            entityObjectMeta.fields.sort(this.fieldSort);

            entityObjectMeta.fields.forEach((f) => {
              this.metaDataServiceImpl.promoteCompositeFields(f);
              this.metaDataServiceImpl.updatePickerFieldMeta(f);
            });

            // Remove to-many / to-one fields that are beyond depth as they will not be
            // populated anyway, unless the concrete impl grants the field extra depth.
            entityObjectMeta.fields = entityObjectMeta.fields.filter((f) =>
              this.includeAssociatedField(f, entityType ?? '', deep, monitor.maxDepth)
            );

            let moreRequests = false;
            const moreAssociatedFields = entityObjectMeta.fields.filter((f) => this.isAssociatedField(f));
            if (moreAssociatedFields.length === 0) {
              if (monitor.count === 0) {
                done.next(true);
                done.complete();
              }
              return;
            }

            moreAssociatedFields.forEach((tmf) => {
              const alreadyPulled = this.getAssociatedFieldMeta(endpoint, tmf, monitor, deep + 1, done);
              if (!alreadyPulled) {
                moreRequests = true;
              }
            });

            if (!moreRequests) {
              if (monitor.count === 0) {
                done.next(true);
                done.complete();
              }
            }
          },
          error: (err) => {
            monitor.count--;
            console.error(`Error getting associated field data for ${entityType}`, err);
            done.error(err);
          },
        });
    }

    return !!found;
  }

  private updateAssociatedField(
    associatedField: IFieldMeta,
    assocEntityList: { entityType: string; result: Readonly<IEntityMeta> }[],
    depth: number,
    maxDepth: number
  ): void {
    const entity = this.metaDataServiceImpl.findAssociatedEntity(associatedField, assocEntityList);
    if (!entity) {
      this.debug('Could not find entity for', associatedField.path, associatedField.associatedEntity);
      return;
    }

    this.updateAssociatedCustomObjectLabel(associatedField, entity);

    const clonedFields = this.cloneFields(entity, depth, maxDepth);
    this.setChildren(associatedField, clonedFields);

    // assumption that composite fields only every contain scalar fields and so don't need recursively populating
    associatedField.fields!.forEach((f: IFieldMeta) => {
      if (this.isAssociatedField(f)) {
        this.updateAssociatedField(f, assocEntityList, depth + 1, maxDepth);
      }
    });
  }

  private updateAssociatedCustomObjectLabel(associatedField: IFieldMeta, entity: { entityType: string; result: Readonly<IEntityMeta> }) {
    if (entity.result.label && associatedField.associatedEntity?.toLowerCase().includes('customobject')) {
      associatedField.label = entity.result.label;
    }
  }

  private cloneFields(entity: { entityType: string; result: Readonly<IEntityMeta> }, depth: number, maxDepth: number) {
    const clone = (f) => {
      return { ...f, fields: f.fields?.map((f) => ({ ...f })) };
    };

    const cloned: IFieldMeta[] = [];
    entity.result.fields.forEach((f: IFieldMeta) => {
      // exclude to-many and to-one fields if they're beyond depth, unless the concrete
      // impl grants extra depth (the same question the fetch phase asked)
      if (this.includeAssociatedField(f, entity.entityType, depth, maxDepth)) {
        cloned.push(clone(f));
      }
    });

    return cloned;
  }

  private fieldSort(a: IFieldMeta, b: IFieldMeta) {
    // sort alphabetically
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  }

  private setChildren(parent: IFieldMeta, children: IFieldMeta[]): void {
    parent.fields = children;
    this.setParent(children, parent);
  }

  private setParent(fields: IFieldMeta[], parent?: IFieldMeta): void {
    fields.forEach((field) => {
      field.parent = parent;
      if (parent) {
        field.path = field.parent?.path + '.' + field.path.split('.').pop();
      } // child path to parent
      if (field.fields) {
        this.setParent(field.fields, field);
      }
    });
  }

  private isAssociatedField(field: IFieldMeta): boolean {
    return field.type === 'to-many' || field.type === 'to-one';
  }

  /**
   * Single source of truth for the depth gate, used by both the fetch phase and the
   * assembly phase so the two can never disagree. A field beyond maxDepth is only kept
   * if the concrete implementation grants it extra depth.
   */
  private includeAssociatedField(field: IFieldMeta, parentEntityType: string, depth: number, maxDepth: number): boolean {
    if (!this.isAssociatedField(field)) {
      return true;
    }
    const extraDepth = this.metaDataServiceImpl.getExtraDepth?.(field, parentEntityType) ?? 0;
    return depth + 1 <= maxDepth + extraDepth;
  }

  // deep copy each field object
  private mapObjectMeta(objectMeta: IObjectMeta) {
    return objectMeta.fields.map((field) => ({ ...field }));
  }

  // TODO: remove this when we're happy it's working
  private debug(message?: any, ...optionalParams: any[]): void {
    if (environment.isDev) {
      console.debug(message, ...optionalParams);
    }
  }
}
